import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { userCan } from "../lib/permissions";

const StoreContext = createContext(null);
const STORE_KEY = "pos_store_id";
// When a staff user gets an admin PIN to switch to a different store,
// we stash the target store id in sessionStorage so refresh() can
// distinguish "staff just authorised an override" from "staff loaded
// the app fresh". Without it, the post-PIN window.location.reload()
// triggers refresh() which would otherwise re-pin them to their home
// store and silently undo the override. sessionStorage clears on tab
// close / logout, so the override is correctly scoped to the session.
const PIN_OVERRIDE_KEY = "pos_pin_verified_store";

export function StoreProvider({ children }) {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [currentStoreId, setCurrentStoreId] = useState(
    () => localStorage.getItem(STORE_KEY) || ""
  );
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setStores([]);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/stores");
      setStores(data);
      if (data.length > 0) {
        const saved = localStorage.getItem(STORE_KEY);
        const validSaved = saved && data.find((s) => s.id === saved);
        const homeStore = user.store_id && data.find((s) => s.id === user.store_id) ? user.store_id : null;
        // PIN-verified override (staff only) — set by setStore() when an
        // admin PIN unlocks a non-home store, survives the
        // window.location.reload() that follows the PIN modal.
        const pinOverride = sessionStorage.getItem(PIN_OVERRIDE_KEY);
        const validPinOverride = pinOverride && data.find((s) => s.id === pinOverride);

        // Priority when deciding which store to land on:
        //   • STAFF — respect their last-picked store (localStorage) as
        //     long as it's still in their assigned set (home OR any
        //     entry in store_ids). Falls back to home, then first store.
        //     Prior to the multi-store change this always reset to
        //     `homeStore`, which bounced multi-store staff back to
        //     store A every time refresh() fired (route change,
        //     user context refresh, redeploy hot-reload…) even though
        //     they had legitimate access to store B.
        //     EXCEPTION: if this session already authorised a store
        //     OUTSIDE the assigned set via admin PIN, respect that
        //     override until the tab closes or the staff logs out.
        //   • ADMIN — last-used store wins (localStorage), then home
        //     store if any, then first store. Admins are allowed to roam.
        const allowedForStaff = (user.store_ids && user.store_ids.length > 0)
          ? user.store_ids
          : (user.store_id ? [user.store_id] : []);
        const savedIsAllowedForStaff = validSaved && allowedForStaff.includes(saved);
        let nextId;
        if (user.role === "staff") {
          if (validPinOverride) nextId = pinOverride;
          else if (savedIsAllowedForStaff) nextId = saved;
          else nextId = homeStore || allowedForStaff[0] || data[0].id;
        } else {
          if (validSaved) nextId = saved;
          else if (homeStore) nextId = homeStore;
          else nextId = data[0].id;
        }
        if (nextId !== currentStoreId) setCurrentStoreId(nextId);
        localStorage.setItem(STORE_KEY, nextId);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [user, currentStoreId]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [user]);

  // Switch the active store.
  //
  // Authorization:
  //   • Admins can switch freely.
  //   • Staff can ONLY land on their assigned home store. Any attempt
  //     to switch to a different store needs an admin PIN — caller
  //     should put up the PinPromptModal first, then call setStore
  //     with `{ pinVerified: true }` after the PIN is validated against
  //     the backend. We persist the PIN-verified target in
  //     sessionStorage so the override survives the page reload that
  //     usually follows a switch.
  //
  // To keep this hook backwards-compatible (UI components that call
  // setStore(id) without options should still work for admins), we
  // throw an Error here for staff trying to switch without a PIN —
  // the caller is expected to catch and surface the PIN dialog.
  const setStore = (id, opts = {}) => {
    if (id === currentStoreId) return;
    // Multi-store staff — allow free switching between any store in the
    // user's `store_ids` list without an admin PIN. Falls back to the
    // legacy single-store `store_id` for accounts that predate multi-
    // store. Admins and staff with the `store.switch` permission remain
    // unrestricted; everyone else still needs admin PIN authorisation
    // via Layout's PinPromptModal to reach a store outside their set.
    const allowedIds = (user?.store_ids && user.store_ids.length > 0)
      ? user.store_ids
      : (user?.store_id ? [user.store_id] : []);
    const canSwitchFreely = user?.role === "admin" || userCan(user, "store.switch");
    const isInAllowedSet = allowedIds.includes(id);
    if (!canSwitchFreely && !isInAllowedSet && !opts.pinVerified) {
      const e = new Error("PIN_REQUIRED");
      e.code = "PIN_REQUIRED";
      throw e;
    }
    if (user?.role === "staff") {
      if (opts.pinVerified && !allowedIds.includes(id)) {
        // PIN unlocked a store OUTSIDE the assigned set — persist the
        // override so it survives the reload.
        sessionStorage.setItem(PIN_OVERRIDE_KEY, id);
      } else if (allowedIds.includes(id)) {
        // staff switching within their assigned set — no override needed
        sessionStorage.removeItem(PIN_OVERRIDE_KEY);
      }
    }
    setCurrentStoreId(id);
    localStorage.setItem(STORE_KEY, id);
  };

  const currentStore = stores.find((s) => s.id === currentStoreId) || stores[0] || null;

  return (
    <StoreContext.Provider value={{ stores, currentStore, currentStoreId, setStore, refresh, loading }}>
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => useContext(StoreContext);
export { STORE_KEY, PIN_OVERRIDE_KEY };
